import { useState } from "react";
import styled, { css } from "styled-components";
import { faMicrochip, faMemory, faServer, faBox } from "@fortawesome/pro-duotone-svg-icons";
import Grid from "assets/homepage/pricing/Grid.svg";
import { Pill } from "@beamcloud/design-system";
import { dashboardLink, demoLink } from "constants/Links";

import { SectionSubtitle, TopSectionContainer, LinkMarketingButton } from "screens/Homepage/styles";
import { typography } from "screens/Homepage/components/typography";
import { BoxedIcon } from "@beamcloud/design-system";
import RateUnitSelect, { TUnit } from "../RateUnitSelect";
import { hexToRgba } from "utils/ColorUtil";

const CPU_RATE_PER_SECOND = 0.00005277;
const RAM_RATE_PER_SECOND = 0.00000555;
const A10G_GPU_RATE_PER_SECOND = 0.00029166;
const RTX4090_GPU_RATE_PER_SECOND = 0.00019166;
const H100_GPU_RATE_PER_SECOND = 0.000972;

const ONE_HOUR_IN_SECONDS = 3600;
const ONE_MINUTE_IN_SECONDS = 60;

const HeroSection: React.FC = () => {
  const [unit, setUnit] = useState<TUnit>("s");

  // Hourly GPU rates are rounded to the tens place
  const renderRateValue = (valuePerSecond: number) => {
    switch (unit) {
      case "h":
        return (valuePerSecond * ONE_HOUR_IN_SECONDS).toFixed(2);
      case "m":
        return (valuePerSecond * ONE_MINUTE_IN_SECONDS).toFixed(2);
      default:
        return valuePerSecond.toFixed(6);
    }
  };

  // Hourly compute rates are rounded to the thousandth place
  const renderComputeRateValue = (valuePerSecond: number) => {
    switch (unit) {
      case "h":
        return (valuePerSecond * ONE_HOUR_IN_SECONDS).toFixed(3);
      case "m":
        return (valuePerSecond * ONE_MINUTE_IN_SECONDS).toFixed(3);
      default:
        return valuePerSecond.toFixed(7);
    }
  };

  return (
    <Container>
      <TitleWrapper>
        <PillWrapper>
          <Pill text="Save up to 80% compared to always-on instances" type="mono-gray" />
        </PillWrapper>
        <Title>Only pay when your code is running</Title>
        <Subtitle>
          The only cloud with checkpoint restore, sandbox snapshots, and sub-second cold starts. Billed by the
          millisecond.
        </Subtitle>
      </TitleWrapper>
      {/* <Notification /> */}
      <ButtonGroup>
        <StyledGetStartedButton href={dashboardLink} buttonTheme="primary">
          Start for free
        </StyledGetStartedButton>
        <StyledSecondaryButton href={demoLink} buttonTheme="secondary">
          Book a Demo
        </StyledSecondaryButton>
      </ButtonGroup>
      <BillingRatesWrapper>
        <BillingRatesCard>
          <RateDetails>
            <StyledBoxedIcon icon={faMicrochip} />
            <RateTitle>CPU</RateTitle>
            <RateDescription>${renderComputeRateValue(CPU_RATE_PER_SECOND)} per core</RateDescription>
          </RateDetails>
          <RateDetails>
            <StyledBoxedIcon icon={faMemory} />
            <RateTitle>RAM</RateTitle>
            <RateDescription>${renderComputeRateValue(RAM_RATE_PER_SECOND)} per GB</RateDescription>
          </RateDetails>
          <RateDetails>
            <StyledBoxedIcon icon={faServer} />
            <RateTitle>RTX 4090</RateTitle>
            <RateDescription>${renderRateValue(RTX4090_GPU_RATE_PER_SECOND)}</RateDescription>
          </RateDetails>
          <RateDetails>
            <StyledBoxedIcon icon={faServer} />
            <RateTitle>A10G</RateTitle>
            <RateDescription>${renderRateValue(A10G_GPU_RATE_PER_SECOND)}</RateDescription>
          </RateDetails>
          <RateDetails>
            <StyledBoxedIcon icon={faServer} />
            <RateTitle>H100</RateTitle>
            <RateDescription>${renderRateValue(H100_GPU_RATE_PER_SECOND)}</RateDescription>
          </RateDetails>
          <RateDetails>
            <StyledBoxedIcon icon={faBox} />
            <RateTitle>File Storage</RateTitle>
            <RateDescription>Included</RateDescription>
          </RateDetails>
          <RateUnitSelect unit={unit} onUnitChange={newUnit => setUnit(newUnit)} />
        </BillingRatesCard>
      </BillingRatesWrapper>
    </Container>
  );
};

export default HeroSection;

const Container = styled(TopSectionContainer)`
  ${({ theme }) => {
    return css`
      background-image: radial-gradient(
          50% 50% at 50% 50%,
          ${hexToRgba(theme.colors.gray1, 0)} 0%,
          ${hexToRgba(theme.colors.gray1, 0.24)} 70%,
          ${hexToRgba(theme.colors.gray1, 1)} 100%
        ),
        url(${Grid});
      background-size: 100% 100%, cover;
      background-position: center, center;
      background-repeat: no-repeat;
    `;
  }}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4px 16px 0px 16px;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 8px 20px 0px 20px;
    gap: 8px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    padding: 12px 0px 0px 0px;
    gap: 12px;
  }
`;

const Title = styled.h1`
  ${({ theme }) => {
    return css`
      all: unset;
      color: ${theme.colors.gray12};
      width: 100%;
      max-width: 100%;
      white-space: normal;
      text-align: center;
      font-size: 22px;
      font-weight: 360;
      line-height: 1.1;
      font-family: "Sequel Sans", "Inter var", system-ui;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
      margin: 0;
      padding: 0;

      @media (min-width: ${theme.breakpoints.sm}px) {
        font-size: 28px;
        white-space: nowrap;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        ${typography.title48};
      }

      @media (min-width: ${theme.breakpoints.lg}px) {
        ${typography.title56};
      }

      @media (min-width: ${theme.breakpoints.xl}px) {
        ${typography.title60};
      }
    `;
  }}
`;

const Subtitle = styled(SectionSubtitle)`
  ${({ theme }) => {
    return css`
      text-align: center;
      font-size: 13px;
      width: 100%;
      max-width: 100%;
      line-height: 1.2;
      margin: 8px 0 0 0;
      padding: 0;
      font-weight: 500;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;

      @media (min-width: ${theme.breakpoints.sm}px) {
        font-size: 15px;
        margin-top: 12px;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        font-size: 17px;
        margin-top: 16px;
      }

      @media (min-width: ${theme.breakpoints.lg}px) {
        font-size: 19px;
      }
    `;
  }}
`;

const BillingRatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 20px;
  width: 100%;
`;

const BillingRatesCard = styled.div`
  ${({ theme }) => {
    return css`
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 16px 12px 24px;
      box-sizing: border-box;
      gap: 20px;
      width: 100%;
      max-width: 100%;
      background-color: ${theme.colors.gray2};
      border: 1px solid ${theme.colors.gray6};
      box-shadow: ${theme.shadows.sm};
      border-radius: 8px;
      margin: 0 8px 24px 8px;

      @media (min-width: ${theme.breakpoints.sm}px) {
        padding: 20px 24px 40px;
        gap: 32px;
        max-width: 640px;
        margin: 0 0 24px 0;
      }
    `;
  }}
`;

const StorageCard = styled(BillingRatesCard)`
  padding: 20px 24px;
`;

const RateDetails = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 8px 0px;
      gap: 12px;
      width: 100%;

      @media (min-width: ${theme.breakpoints.sm}px) {
        padding: 0px;
        gap: 16px;
      }
    `;
  }}
`;

const StyledBoxedIcon = styled(BoxedIcon).attrs({
  dimension: 28,
  iconSize: "sm",
})`
  && {
    ${({ theme }) => {
      return css`
        display: inline-flex;
        background-color: ${theme.colors.gray4};
        border: 1px solid ${theme.colors.gray8};
        color: ${theme.colors.gray12};
        flex-shrink: 0;

        @media (min-width: ${theme.breakpoints.sm}px) {
          width: 36px;
          height: 36px;
        }
      `;
    }}
  }
`;

const RateTitle = styled.span`
  ${({ theme }) => {
    return css`
      flex-grow: 1;
      color: ${theme.colors.gray12};
      font-size: 16px;
      font-weight: 500;
      min-width: 0;
      flex-shrink: 1;

      @media (min-width: ${theme.breakpoints.sm}px) {
        ${typography.title20};
      }
    `;
  }}
`;

const RateDescription = styled.span`
  ${({ theme }) => {
    return css`
      color: ${theme.colors.gray11};
      font-size: 14px;
      font-weight: 400;
      text-align: right;
      flex-shrink: 0;
      min-width: 80px;

      @media (min-width: ${theme.breakpoints.sm}px) {
        ${typography.textLgLight};
        text-align: left;
        min-width: auto;
      }
    `;
  }}
`;

const CircleBoxedIcon = styled(StyledBoxedIcon)`
  ${({ theme }) => {
    return css`
      && {
        border-radius: 50%;
        box-shadow: ${theme.shadows.sm};
      }
    `;
  }}
`;

const ButtonGroup = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 16px;
      width: 100%;
      margin: -8px 0 0 0;
      padding: 0 16px;
      box-sizing: border-box;

      @media (max-width: ${theme.breakpoints.sm}px) {
        flex-direction: column;
        gap: 12px;
        padding: 0 20px;
      }
    `;
  }}
`;

const PillWrapper = styled.div`
  ${({ theme }) => {
    return css`
      margin-bottom: 8px;
      width: 100%;
      max-width: 100%;
      display: flex;
      justify-content: center;

      & > div {
        padding: 6px 12px;
        font-size: 12px;
        font-weight: 600;
        background: ${theme.colors.blue4};
        color: ${theme.colors.blue11};
        border: 1px solid ${theme.colors.blue5};
        border-radius: 20px;
        box-shadow: 0px 1px 3px rgba(55, 125, 255, 0.15);
        transform: scale(0.9);
        white-space: nowrap;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;

        @media (min-width: ${theme.breakpoints.sm}px) {
          font-size: 14px;
          padding: 8px 16px;
          transform: scale(0.95);
        }
      }
    `;
  }}
`;

const StyledGetStartedButton = styled(LinkMarketingButton)`
  ${({ theme }) => {
    return css`
      && {
        height: 48px;
        padding: 12px 24px;
        font-size: 16px;
        font-weight: 600;
        background: ${theme.colors.blue9};
        box-shadow: none;
        border: none;
        border-radius: 6px;
        color: white;

        &:hover {
          background: ${theme.colors.blue10};
        }

        &:active {
          background: ${theme.colors.blue8};
        }
      }
    `;
  }}
`;

const StyledSecondaryButton = styled(LinkMarketingButton)`
  ${({ theme }) => {
    return css`
      && {
        height: 48px;
        padding: 12px 24px;
        font-size: 16px;
        font-weight: 600;
        background: transparent;
        box-shadow: none;
        border: 1px solid ${theme.colors.gray9};
        border-radius: 6px;
        color: ${theme.colors.gray11};

        &:hover {
          background: ${theme.colors.gray3};
          border-color: ${theme.colors.gray10};
        }

        &:active {
          background: ${theme.colors.gray4};
        }
      }
    `;
  }}
`;
